import { FC } from 'react';

import { useRecoilValue } from 'recoil';

import { Icon } from '@shared/components/icons';
import { useAuth } from '@shared/state/hooks/useAuth';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import aplusTopLogo from 'src/assets/logo/aplusTop.png';

import { giftCartAtom } from '../../state/giftCart.atom';
import {
  giftStoreNavBackUrlAtom,
  giftStoreNavBarScrolledAtom,
  giftStoreNavBarTitleAtom,
  giftStoreNavBarTransparentAtom,
  giftStoreNavBarUseAtom,
} from './giftStoreNavBar.atom';

export const GiftStoreNavBar: FC = function GiftStoreNavBar() {
  const { Back } = Icon();
  const location = useLocation();
  const pathName = location.pathname;
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const bugoId = searchParams.get('bugoId');
  const { userProfile } = useAuth();

  const title = useRecoilValue(giftStoreNavBarTitleAtom);
  const navBarUse = useRecoilValue(giftStoreNavBarUseAtom);
  const backUrl = useRecoilValue(giftStoreNavBackUrlAtom);
  const isTransparent = useRecoilValue(giftStoreNavBarTransparentAtom);
  const isScrolled = useRecoilValue(giftStoreNavBarScrolledAtom);
  const cartItems = useRecoilValue(giftCartAtom);

  // userProfile이 없으면 구매내역 버튼 비활성화
  const hasUserProfile = !!userProfile;

  // 장바구니 상품 종류 수 (bugoId로 필터링)
  const cartItemCount = bugoId
    ? cartItems.filter((item) => item.bugoId === bugoId).length
    : cartItems.length;

  // 투명 모드에서 스크롤 안된 상태인지
  const isTransparentActive = isTransparent && !isScrolled;

  const navLeft = () => {
    // 메인 페이지이거나 title이 없으면 로고 표시
    if (pathName === '/gift-store' || title === '') {
      return <img src={aplusTopLogo} alt="로고" className="h-9 object-contain" />;
    } else {
      // 하위 페이지에서는 뒤로가기 + 타이틀
      return (
        <div className="flex items-center">
          <button
            onClick={() => {
              backUrl ? navigate(backUrl) : navigate(-1);
            }}
            className="center-box wh-10"
          >
            {isTransparentActive ? (
              // 투명모드: 흰색 아이콘 + 그림자
              <svg
                className="h-6 w-6 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]"
                fill="none"
                stroke="white"
                strokeWidth={2}
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
            ) : (
              <Back />
            )}
          </button>
          {/* 투명모드에서 스크롤 후에만 타이틀 표시 */}
          {(!isTransparent || isScrolled) && (
            <p className="bugo-h1 max-w-[200px] truncate">{title}</p>
          )}
        </div>
      );
    }
  };

  const navRight = () => {
    const iconClass = isTransparentActive
      ? 'h-6 w-6 drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)]'
      : 'h-6 w-6';
    const strokeColor = isTransparentActive ? 'white' : 'currentColor';

    return (
      <div className="flex items-center gap-1">
        {/* 홈 버튼 */}
        <button
          className="center-box wh-10"
          onClick={() => {
            navigate(`/gift-store${bugoId ? `?bugoId=${bugoId}` : ''}`);
          }}
          title="홈"
        >
          <svg
            className={iconClass}
            fill="none"
            stroke={strokeColor}
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
            />
          </svg>
        </button>
        {/* 구매내역 버튼 */}
        <button
          className={`center-box wh-10 ${!hasUserProfile ? 'opacity-30' : ''}`}
          onClick={() => {
            if (hasUserProfile) {
              navigate(`/gift-store/orders${bugoId ? `?bugoId=${bugoId}` : ''}`);
            }
          }}
          disabled={!hasUserProfile}
          title={hasUserProfile ? '구매내역' : '구매 후 이용 가능'}
        >
          <svg
            className={iconClass}
            fill="none"
            stroke={strokeColor}
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </button>
        {/* 장바구니 버튼 */}
        <button
          className="center-box wh-10 relative"
          onClick={() => {
            navigate(`/gift-store/cart${bugoId ? `?bugoId=${bugoId}` : ''}`);
          }}
          title="장바구니"
        >
          {cartItemCount > 0 && (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {cartItemCount > 99 ? '99+' : cartItemCount}
            </span>
          )}
          <svg
            className={iconClass}
            fill="none"
            stroke={strokeColor}
            strokeWidth={1.5}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
            />
          </svg>
        </button>
      </div>
    );
  };

  if (!navBarUse) {
    return null;
  }

  return (
    <div
      className={`fixed z-20 flex h-14 w-full max-w-inherit items-center justify-between px-4 transition-all duration-200 ${
        isTransparentActive
          ? 'bg-gradient-to-b from-black/20 to-transparent'
          : 'bg-white shadow-sm'
      }`}
    >
      {navLeft()}
      {navRight()}
    </div>
  );
};
